import { installedOrder } from './api/order/api';
import { Order } from './api/order/types';
import { Device } from './device';
import { InstallProperties } from './interfaces/properties.interface';
import { ISelectionForm } from './interfaces/selectionForm.interface';
import { ContextFrom, EventFrom, SnapshotFrom, assign, setup } from 'xstate';

export interface StateContext {
  errorMessage: string;
  selectionForm: ISelectionForm | null;
  device: Device | null;
  order: Order | null;
  properties: InstallProperties | null;
  headsetInstalled: boolean;
  headsetCleaned: boolean;
  headsetValidated: boolean;
  isCustom: boolean;
}

export const stateMachine = setup({
  types: {
    context: {} as StateContext,
    input: {} as {
      isCustom: boolean;
    },
    events: {} as
      | {
          type: 'headset.selectionForm.update';
          data: ISelectionForm | null;
        }
      | {
          type: 'headset.selected';
          data: Device;
        }
      | {
          type: 'headset.selection.cancel';
        }
      | {
          type: 'headset.cleaned';
        }
      | {
          type: 'headset.installed';
        }
      | {
          type: 'headset.validated';
        }
      | {
          type: 'order.found';
          value: Order;
        }
      | {
          type: 'properties.set';
          value: InstallProperties;
        }
      | { type: 'submit' }
      | {
          type: 'close';
        }
      | { type: 'back' }
      | { type: 'restart' }
      | {
          type: 'error';
          value: string;
        },
  },
  guards: {
    selectionComplete: ({ context }) => context.selectionForm !== null,
    orderFound: ({ context }) => context.order !== null && context.properties !== null,
    headsetInstalled: ({ context }) => context.headsetInstalled,
    headsetCleaned: ({ context }) => context.headsetCleaned,
    headsetValidated: ({ context }) => context.headsetValidated,
    resetSkipped: ({ context }) => context.selectionForm !== null && !context.selectionForm.reset,
    isCustom: ({ context }) => context.isCustom,
  },
  actions: {
    updateOrder: async ({ context }) => {
      if (context.order && context.device) {
        if (!context.isCustom) {
          const installedHeadsetSerial = (context.device as Device).getDeviceSerialId();
          if (!installedHeadsetSerial) {
            throw new Error('No headset serial found');
          }
          // Ignore custom orders
          await installedOrder(context.order.id, { installedHeadsetSerial });
        }
        await context.device.releaseDevice();
      }
    },
  },
}).createMachine({
  id: 'feedback',
  initial: 'selection',
  context: ({ input }) => ({
    errorMessage: '',
    selectionForm: null,
    device: null,
    order: null,
    properties: null,
    headsetInstalled: false,
    headsetCleaned: false,
    headsetValidated: false,
    isCustom: input.isCustom,
  }),
  states: {
    selection: {
      on: {
        'headset.selectionForm.update': {
          actions: assign({
            selectionForm: ({ event }) => event.data,
          }),
        },
        'headset.selected': {
          actions: assign({
            device: ({ event }) => event.data,
          }),
        },
        error: {
          actions: assign({
            errorMessage: ({ event }) => event.value,
          }),
          target: 'error',
        },
        submit: {
          guard: 'selectionComplete',
          target: 'recap',
        },
      },
    },
    recap: {
      always: [{ target: 'prepare', guard: 'isCustom' }],
      on: {
        'order.found': {
          actions: assign({
            order: ({ event }) => event.value,
          }),
        },
        'properties.set': {
          actions: assign({
            properties: ({ event }) => event.value,
          }),
        },
        error: {
          actions: assign({
            errorMessage: ({ event }) => event.value,
          }),
          target: 'error',
        },
        back: { target: 'selection' },
        submit: {
          guard: 'orderFound',
          target: 'clean',
        },
      },
    },
    prepare: {
      on: {
        'order.found': {
          actions: assign({
            order: ({ event }) => event.value,
          }),
        },
        'properties.set': {
          actions: assign({
            properties: ({ event }) => event.value,
          }),
        },
        error: {
          actions: assign({
            errorMessage: ({ event }) => event.value,
          }),
          target: 'error',
        },
        submit: {
          guard: 'orderFound',
          target: 'clean',
        },
      },
    },
    clean: {
      always: [{ target: 'install', guard: 'resetSkipped' }],
      on: {
        'headset.cleaned': {
          actions: assign({
            headsetCleaned: true,
          }),
        },
        error: {
          actions: assign({
            errorMessage: ({ event }) => event.value,
          }),
          target: 'error',
        },
        back: { target: 'selection' },
        submit: {
          guard: 'headsetCleaned',
          target: 'install',
        },
      },
    },
    install: {
      on: {
        'headset.installed': {
          actions: assign({
            headsetInstalled: true,
          }),
        },
        error: {
          actions: assign({
            errorMessage: ({ event }) => event.value,
          }),
          target: 'error',
        },
        back: { target: 'selection' },
        submit: {
          guard: 'headsetInstalled',
          target: 'validate',
        },
      },
    },
    validate: {
      on: {
        'headset.validated': {
          actions: assign({
            headsetValidated: () => true,
          }),
        },
        error: {
          actions: assign({
            errorMessage: ({ event }) => event.value,
          }),
          target: 'error',
        },
        back: { target: 'selection' },
        submit: {
          guard: 'headsetValidated',
          target: 'finished',
        },
      },
    },
    error: {},
    finished: {
      entry: [{ type: 'updateOrder' }],
      on: {
        restart: {
          target: 'selection',
        },
      },
    },
    closed: {
      on: {
        restart: {
          actions: assign({
            errorMessage: '',
            selectionForm: null,
            headsetInstalled: false,
            headsetCleaned: false,
            headsetValidated: false,
          }),
          target: 'selection',
        },
      },
    },
  },
  on: {
    close: '.closed',
  },
});

export type StateMachine = typeof stateMachine;
export type StateMachineContext = ContextFrom<StateMachine>;
export type StateMachineSnapshot = SnapshotFrom<StateMachine>;
export type StateMachineEvent = EventFrom<StateMachine>;
